import React, { useEffect, useMemo, useRef, useState } from 'react';
import rawOddsService from '../../../utils/services/rawOddsService';
import communityTipsService, { parseStake, isStakeInRange, STAKE_LIMITS } from '../../../utils/services/communityTipsService';
import { BookmakerLogo, BOOKMAKER_DISPLAY_NAMES } from '../../HotProps/bookmakerAssets';
import SubscriptionRequired from '../../common/SubscriptionRequired';

// Maps a prop-tool market key to the stat words used in the raw-odds market
// strings (e.g. "25+ Disposals", "1+ Goal"). Both singular/plural handled.
const MARKET_ALIASES = {
    disposals: ['disposals', 'disposal'],
    goals: ['goals', 'goal'],
    marks: ['marks', 'mark'],
    tackles: ['tackles', 'tackle'],
    kicks: ['kicks', 'kick'],
    handballs: ['handballs', 'handball'],
    clearances: ['clearances', 'clearance'],
    hit_outs: ['hit outs', 'hit out', 'hitouts', 'hitout'],
};

// "25+ Disposals" -> { threshold: 25, stat: "disposals" }
function parseMarketLine(marketStr) {
    const m = /^\s*(\d+(?:\.\d+)?)\s*\+?\s*(.+?)\s*$/.exec(marketStr || '');
    if (!m) return null;
    return { threshold: Number(m[1]), stat: m[2].trim().toLowerCase() };
}

function fmtOdds(v) {
    return v == null ? '—' : v.toFixed(2);
}

function fmtLine(n) {
    if (n == null) return '—';
    return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function ImpliedEdgeTable({ playerName, marketKey, marketLabel, userLine, datasetValues = [], onSelectLine, canTip = false, preferredBookmaker = null }) {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedBook, setSelectedBook] = useState(null);
    // Ensures a deep-linked bookmaker preference is applied at most once.
    const preferredAppliedRef = useRef(false);

    // Inline community tipping — one unit-stake input per line. One stake = a
    // SINGLE tip, two or more = a LADDER (legs share the selected bookmaker).
    const [stakes, setStakes] = useState({});
    const [tipSubmitting, setTipSubmitting] = useState(false);
    const [tipError, setTipError] = useState(null);
    const [tipSuccess, setTipSuccess] = useState(null);

    // Fetch every market/bookmaker for this player (once per player).
    useEffect(() => {
        if (!playerName) {
            setEntries([]);
            setError(null);
            setLoading(false);
            return;
        }
        let active = true;
        setLoading(true);
        setError(null);
        rawOddsService.getPlayerOdds(playerName).then((res) => {
            if (!active) return;
            if (res.success) {
                setEntries(res.data?.odds || []);
                setError(null);
            } else {
                setEntries([]);
                setError(res);
            }
            setLoading(false);
        });
        return () => { active = false; };
    }, [playerName]);

    // Entries that belong to the currently selected stat type.
    const marketEntries = useMemo(() => {
        const aliases = MARKET_ALIASES[marketKey] || [marketKey];
        return entries.filter((e) => {
            const parsed = parseMarketLine(e.market);
            return parsed && aliases.includes(parsed.stat);
        });
    }, [entries, marketKey]);

    // Bookmakers offering at least one non-derived line for this stat.
    const statBookmakers = useMemo(() => {
        const set = new Set();
        marketEntries.forEach((e) => {
            Object.keys(e.odds || {}).forEach((bk) => {
                if (e.isDerived?.[bk] === true) return;
                if (e.odds[bk] != null) set.add(bk);
            });
        });
        return Array.from(set).sort();
    }, [marketEntries]);

    // Line → odds map for a given bookmaker (derived prices excluded).
    const linesForBook = useMemo(() => {
        const out = {};
        statBookmakers.forEach((bk) => {
            const map = new Map();
            marketEntries.forEach((e) => {
                if (e.isDerived?.[bk] === true) return;
                const odds = e.odds?.[bk];
                if (odds == null) return;
                const parsed = parseMarketLine(e.market);
                if (parsed) map.set(parsed.threshold, odds);
            });
            out[bk] = map;
        });
        return out;
    }, [marketEntries, statBookmakers]);

    // threshold → canonical market string (e.g. 25 → "25+ Disposals"). Used to
    // submit tips with the exact market name the odds cache expects.
    const marketByThreshold = useMemo(() => {
        const m = new Map();
        marketEntries.forEach((e) => {
            const parsed = parseMarketLine(e.market);
            if (parsed) m.set(parsed.threshold, e.market);
        });
        return m;
    }, [marketEntries]);

    const total = datasetValues.length;
    const hitRate = (x) => (total ? datasetValues.filter((v) => v >= x).length / total : null);

    // Best implied edge each bookmaker offers across its lines, using the Snipe
    // Feed formula (hit_rate_percentage − 100 / odds). Drives the default selection.
    const bestEdgeByBook = useMemo(() => {
        const out = {};
        statBookmakers.forEach((bk) => {
            const map = linesForBook[bk];
            let best = null;
            if (map && total) {
                map.forEach((odds, threshold) => {
                    if (odds == null || odds <= 0) return;
                    const hr = datasetValues.filter((v) => v >= threshold).length / total;
                    const edge = hr * 100 - 100 / odds;
                    if (best == null || edge > best) best = edge;
                });
            }
            out[bk] = best;
        });
        return out;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statBookmakers, linesForBook, datasetValues, total]);

    // Default the selection to the bookmaker offering the best implied edge
    // (falling back to the most lines when no dataset edge is available).
    useEffect(() => {
        if (!statBookmakers.length) {
            setSelectedBook(null);
            return;
        }
        if (selectedBook && statBookmakers.includes(selectedBook)) return;
        // On the first deep-linked render, honour the requested bookmaker (the
        // best book from Snipe Feed / Odds Screen / Hot Props) when it's offered.
        if (!preferredAppliedRef.current && preferredBookmaker) {
            preferredAppliedRef.current = true;
            const match = statBookmakers.find((bk) => bk.toLowerCase() === preferredBookmaker);
            if (match) { setSelectedBook(match); return; }
        }
        let best = statBookmakers[0];
        let bestScore = -Infinity;
        statBookmakers.forEach((bk) => {
            const e = bestEdgeByBook[bk];
            const score = e == null ? -Infinity : e;
            if (score > bestScore) { bestScore = score; best = bk; }
        });
        if (bestScore === -Infinity) {
            let bestCount = -1;
            statBookmakers.forEach((bk) => {
                const c = linesForBook[bk]?.size || 0;
                if (c > bestCount) { bestCount = c; best = bk; }
            });
        }
        setSelectedBook(best);
    }, [statBookmakers, selectedBook, bestEdgeByBook, linesForBook, preferredBookmaker]);

    // Build the columns: user's line first, then each bookmaker line.
    const columns = useMemo(() => {
        const bookMap = (selectedBook && linesForBook[selectedBook]) || new Map();
        const userCeil = userLine != null ? Math.ceil(userLine) : null;
        const cols = [];
        if (userLine != null) {
            cols.push({
                key: 'user',
                x: userLine,
                label: fmtLine(userLine),
                isUser: true,
                odds: userCeil != null && bookMap.has(userCeil) ? bookMap.get(userCeil) : null,
                market: userCeil != null ? marketByThreshold.get(userCeil) || null : null,
            });
        }
        Array.from(bookMap.keys())
            .sort((a, b) => a - b)
            .forEach((t) => {
                if (userLine != null && t === userCeil) return; // represented by the user column
                cols.push({ key: `b${t}`, x: t, label: `${fmtLine(t)}+`, isUser: false, odds: bookMap.get(t), market: marketByThreshold.get(t) || null });
            });
        return cols;
    }, [selectedBook, linesForBook, userLine, marketByThreshold]);

    // Reset any entered stakes / tip status when the underlying context changes.
    useEffect(() => {
        setStakes({});
        setTipError(null);
        setTipSuccess(null);
    }, [selectedBook, marketKey, playerName, userLine]);

    // Derive the tip from the entered stakes: legs with odds + a stake value.
    const tipState = useMemo(() => {
        const entered = columns
            .filter((c) => c.odds != null && c.market)
            .map((c) => ({ key: c.key, market: c.market, odds: c.odds, label: c.label, stakeStr: stakes[c.key] ?? '' }))
            .filter((c) => String(c.stakeStr).trim() !== '');
        const count = entered.length;
        const type = count >= 2 ? 'LADDER' : 'SINGLE';
        const allValid = count > 0 && entered.every((l) => {
            const n = parseStake(l.stakeStr);
            return n != null && isStakeInRange(n);
        });
        let combinedOdds = 1;
        let totalStake = 0;
        let totalPayout = 0;
        entered.forEach((l) => {
            const n = parseStake(l.stakeStr) || 0;
            combinedOdds *= l.odds || 1;
            totalStake += n;
            totalPayout += n * (l.odds || 0);
        });
        return { entered, count, type, allValid, combinedOdds, totalStake, totalPayout, tooMany: count > 10 };
    }, [columns, stakes]);

    const canSubmitTip = canTip && !tipSubmitting && !tipSuccess && selectedBook
        && tipState.count >= 1 && tipState.count <= 10 && tipState.allValid;

    const setStake = (key, value) => {
        setTipError(null);
        setTipSuccess(null);
        setStakes((prev) => ({ ...prev, [key]: value }));
    };

    const handleTip = async () => {
        if (!canSubmitTip) return;
        setTipSubmitting(true);
        setTipError(null);
        setTipSuccess(null);

        const legs = tipState.entered.map((l) => ({ market: l.market, stake: l.stakeStr }));
        const input = legs.length === 1
            ? {
                tip_type: 'SINGLE',
                tipped_tool: 'PROP_TOOL',
                player_name: playerName,
                market: legs[0].market,
                bookmaker: selectedBook,
                stake: legs[0].stake,
            }
            : {
                tip_type: 'LADDER',
                tipped_tool: 'PROP_TOOL',
                player_name: playerName,
                bookmaker: selectedBook,
                legs,
            };

        const res = await communityTipsService.submitTip(input);
        if (res.success) {
            setTipSuccess({
                message: legs.length === 1
                    ? 'Single tip submitted to the community feed.'
                    : `${legs.length}-leg ladder submitted to the community feed.`,
            });
            setStakes({});
        } else {
            setTipError({ message: res.message || 'Failed to submit tip.', code: res.errorCode });
        }
        setTipSubmitting(false);
    };

    return (
        <div className="mt-5 rounded-2xl border border-slate-700/50 bg-slate-950/30 p-3 md:p-4">
            {/* Header + bookmaker selector */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-2.5">
                    <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-slate-400">Implied Edge</span>
                    <span className="text-[10px] font-mono text-slate-600">vs {marketLabel} lines</span>
                </div>
                {statBookmakers.length > 0 && (
                    <div
                        className="flex items-center gap-1.5 flex-nowrap overflow-x-auto md:flex-wrap md:overflow-visible w-full md:w-auto -mx-1 px-1 md:mx-0 md:px-0"
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    >
                        {statBookmakers.map((bk) => {
                            const active = bk === selectedBook;
                            return (
                                <button
                                    key={bk}
                                    type="button"
                                    onClick={() => setSelectedBook(bk)}
                                    title={BOOKMAKER_DISPLAY_NAMES[bk] || bk}
                                    className={`flex flex-shrink-0 items-center gap-1.5 pl-1 pr-2 py-1 rounded-lg border transition-all ${
                                        active
                                            ? 'bg-emerald-500/10 border-emerald-500/40'
                                            : 'bg-slate-900/50 border-slate-700/60 hover:border-slate-600'
                                    }`}
                                >
                                    <BookmakerLogo bookmaker={bk} size={16} />
                                    <span className={`text-[10px] font-mono ${active ? 'text-emerald-300' : 'text-slate-400'}`}>
                                        {BOOKMAKER_DISPLAY_NAMES[bk] || bk}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {loading ? (
                <div className="flex items-center gap-2.5 py-6 justify-center">
                    <div className="w-4 h-4 border-2 border-slate-700 border-t-emerald-400 rounded-full animate-spin" />
                    <span className="text-xs font-mono text-slate-500">Loading bookmaker odds…</span>
                </div>
            ) : error ? (
                error.isSubscriptionError ? (
                    <SubscriptionRequired feature="live odds" variant="inline" />
                ) : (
                    <div className="py-6 text-center text-xs font-mono text-slate-500">
                        {error.message || 'Unable to load bookmaker odds.'}
                    </div>
                )
            ) : columns.length === 0 ? (
                <div className="py-6 text-center text-xs font-mono text-slate-500">
                    {statBookmakers.length === 0
                        ? `No bookmaker lines currently available for ${marketLabel}.`
                        : 'Set a line above to compare against the bookmaker.'}
                </div>
            ) : (
                <div className="overflow-x-auto styled-scrollbar rounded-xl border border-slate-800/70">
                    <table className="w-full text-sm border-separate" style={{ borderSpacing: 0 }}>
                        <thead>
                            <tr>
                                <th className="hidden md:table-cell sticky left-0 z-10 bg-slate-900/80 backdrop-blur text-center text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500 font-medium px-3 py-2.5 border-b border-slate-800/70">
                                    Line
                                </th>
                                {columns.map((c) => (
                                    <th
                                        key={c.key}
                                        className={`p-0 text-center border-b border-slate-800/70 ${c.isUser ? 'bg-emerald-500/[0.07]' : 'bg-slate-900/40'}`}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => onSelectLine && onSelectLine(c)}
                                            title="Set as graph line"
                                            className="group/col w-full px-3 py-2 transition-colors hover:bg-emerald-500/[0.08] focus:outline-none"
                                        >
                                            <div className={`text-sm font-mono font-bold tabular-nums transition-colors ${c.isUser ? 'text-emerald-300' : 'text-white group-hover/col:text-emerald-300'}`}>
                                                {c.label}
                                            </div>
                                            <div className={`text-[9px] font-mono uppercase tracking-wider mt-0.5 ${
                                                c.isUser ? 'text-emerald-400/70' : 'text-slate-600 group-hover/col:text-emerald-400/70'
                                            }`}>
                                                {c.isUser ? 'Your line' : 'Set line'}
                                            </div>
                                        </button>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {/* Row 1 — hit rate + fair (implied) odds from the dataset */}
                            <tr>
                                <td className="hidden md:table-cell sticky left-0 z-10 bg-slate-900/80 backdrop-blur px-3 py-2.5 text-[11px] font-mono text-slate-300 whitespace-nowrap border-b border-slate-800/40">
                                    Hit Rate <span className="text-slate-600">· Fair</span>
                                </td>
                                {columns.map((c) => {
                                    const hr = hitRate(c.x);
                                    const fair = hr ? 1 / hr : null;
                                    return (
                                        <td key={c.key} className={`px-3 py-2.5 text-center border-b border-slate-800/40 ${c.isUser ? 'bg-emerald-500/[0.04]' : ''}`}>
                                            <div className="text-sm font-mono font-bold tabular-nums text-white">
                                                {hr == null ? '—' : `${(hr * 100).toFixed(0)}%`}
                                            </div>
                                            <div className="text-[10px] font-mono text-slate-500 tabular-nums mt-0.5 whitespace-nowrap">
                                                {fair == null ? '—' : fair.toFixed(2)}
                                                {fair != null && <span className="md:hidden text-slate-600"> (Implied)</span>}
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>

                            {/* Row 2 — bookmaker odds */}
                            <tr>
                                <td className="hidden md:table-cell sticky left-0 z-10 bg-slate-900/80 backdrop-blur px-3 py-2.5 whitespace-nowrap border-b border-slate-800/40">
                                    <div className="flex items-center justify-center">
                                        {selectedBook ? (
                                            <BookmakerLogo bookmaker={selectedBook} size={20} />
                                        ) : (
                                            <span className="text-[11px] font-mono text-slate-400">Odds</span>
                                        )}
                                    </div>
                                </td>
                                {columns.map((c) => (
                                    <td
                                        key={c.key}
                                        className={`px-3 py-2.5 text-center text-sm font-mono font-semibold tabular-nums text-slate-200 border-b border-slate-800/40 ${c.isUser ? 'bg-emerald-500/[0.04]' : ''}`}
                                    >
                                        {fmtOdds(c.odds)}
                                    </td>
                                ))}
                            </tr>

                            {/* Row 3 — implied edge (hit rate % − implied prob of the
                                book price), matching the Snipe Feed formula:
                                hit_rate_percentage − (100 / odds). */}
                            <tr>
                                <td className="hidden md:table-cell sticky left-0 z-10 bg-slate-900/80 backdrop-blur px-3 py-2.5 text-[11px] font-mono text-slate-300 whitespace-nowrap">
                                    Implied Edge
                                </td>
                                {columns.map((c) => {
                                    const hr = hitRate(c.x);
                                    const edge = c.odds != null && c.odds > 0 && hr != null ? hr * 100 - 100 / c.odds : null;
                                    const cls = edge == null ? 'text-slate-600' : edge > 0 ? 'text-emerald-300' : edge < 0 ? 'text-rose-300' : 'text-slate-300';
                                    return (
                                        <td
                                            key={c.key}
                                            className={`px-3 py-2.5 text-center ${c.isUser ? 'bg-emerald-500/[0.04]' : ''}`}
                                        >
                                            <span className={`inline-flex items-center justify-center min-w-[52px] px-2 py-0.5 rounded-md text-[12px] font-mono font-bold tabular-nums ${
                                                edge == null
                                                    ? 'text-slate-600'
                                                    : edge > 0
                                                        ? 'bg-emerald-500/10 ' + cls
                                                        : edge < 0
                                                            ? 'bg-rose-500/10 ' + cls
                                                            : cls
                                            }`}>
                                                {edge == null ? '—' : `${edge > 0 ? '+' : ''}${edge.toFixed(1)}%`}
                                            </span>
                                        </td>
                                    );
                                })}
                            </tr>

                            {/* Tip stake row — a unit-stake input per line (tippers only) */}
                            {canTip && (
                                <tr>
                                    <td className="hidden md:table-cell sticky left-0 z-10 bg-slate-900/80 backdrop-blur px-3 py-2.5 text-[11px] font-mono text-slate-300 whitespace-nowrap border-t border-slate-800/70">
                                        Tip <span className="text-slate-600">· {STAKE_LIMITS.min}–{STAKE_LIMITS.max}U</span>
                                    </td>
                                    {columns.map((c) => {
                                        const tippable = c.odds != null && c.market;
                                        return (
                                            <td key={c.key} className={`px-2 py-2.5 text-center border-t border-slate-800/70 ${c.isUser ? 'bg-emerald-500/[0.04]' : ''}`}>
                                                {tippable ? (
                                                    <StakeInput
                                                        value={stakes[c.key] ?? ''}
                                                        onChange={(v) => setStake(c.key, v)}
                                                        disabled={tipSubmitting || !!tipSuccess}
                                                    />
                                                ) : (
                                                    <span className="text-[11px] font-mono text-slate-700">—</span>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Tip action bar */}
            {canTip && columns.length > 0 && !loading && !error && (
                <div className="mt-3 rounded-xl border border-slate-700/50 bg-slate-950/40 px-3 py-2.5">
                    {tipSuccess ? (
                        <div className="flex items-center gap-2.5">
                            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex-shrink-0">
                                <svg className="w-3.5 h-3.5 text-emerald-400" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                            </span>
                            <span className="text-[12px] font-mono text-emerald-300">{tipSuccess.message}</span>
                            <button
                                type="button"
                                onClick={() => setTipSuccess(null)}
                                className="ml-auto text-[11px] font-mono text-slate-400 hover:text-white transition-colors"
                            >
                                Tip another
                            </button>
                        </div>
                    ) : (
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                            {/* Summary */}
                            <div className="flex items-center gap-2 min-w-0">
                                {tipState.count === 0 ? (
                                    <span className="text-[11px] font-mono text-slate-500">
                                        Enter a unit stake on one or more lines to tip a single or ladder.
                                    </span>
                                ) : (
                                    <>
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono font-semibold uppercase tracking-wider ${
                                            tipState.type === 'LADDER'
                                                ? 'bg-sky-500/15 text-sky-300 border border-sky-500/30'
                                                : 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
                                        }`}>
                                            {tipState.type === 'LADDER' ? `Ladder · ${tipState.count} legs` : 'Single'}
                                        </span>
                                        {tipState.type === 'LADDER' && (
                                            <span className="text-[11px] font-mono text-slate-400 tabular-nums">
                                                @ {tipState.combinedOdds.toFixed(2)}
                                            </span>
                                        )}
                                        {tipState.allValid && (
                                            <span className="text-[11px] font-mono text-slate-500 tabular-nums">
                                                {tipState.totalStake.toFixed(2)}U → <span className="text-emerald-400">{tipState.totalPayout.toFixed(2)}U</span>
                                            </span>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Error */}
                            {tipError && (
                                <span className={`text-[11px] font-mono ${
                                    tipError.code === 'DUPLICATE_TIP' || tipError.code === 'INVALID_BOOKMAKER' || tipError.code === 'LADDER_MARKETS_INVALID'
                                        ? 'text-amber-300'
                                        : 'text-rose-300'
                                }`}>
                                    {tipError.message}
                                </span>
                            )}

                            <button
                                type="button"
                                onClick={handleTip}
                                disabled={!canSubmitTip}
                                className="ml-auto inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-[12px] font-mono font-semibold bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-500 hover:to-teal-500 transition-all shadow-lg shadow-emerald-500/10 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                            >
                                {tipSubmitting ? (
                                    <>
                                        <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Submitting…
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                        </svg>
                                        Tip to Community
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// Compact unit-stake input used in the tip row. Highlights out-of-range values.
function StakeInput({ value, onChange, disabled }) {
    const parsed = parseStake(value);
    const inRange = parsed != null && isStakeInRange(parsed);
    const dirty = value !== '' && value != null;
    const invalid = dirty && !inRange;
    return (
        <div className="relative w-[64px] mx-auto">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[9px] font-mono text-slate-600 pointer-events-none">U</span>
            <input
                type="text"
                inputMode="decimal"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                placeholder="—"
                className={`w-full pl-5 pr-1.5 py-1 bg-slate-900/80 border rounded text-[12px] font-mono text-white text-right tabular-nums placeholder-slate-600 focus:outline-none transition-colors ${
                    invalid ? 'border-rose-500/60 focus:border-rose-400' : 'border-slate-700/70 focus:border-emerald-500/60'
                } disabled:opacity-40 disabled:cursor-not-allowed`}
            />
        </div>
    );
}

export default ImpliedEdgeTable;
